import type { FC } from 'react';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { fDateTime } from 'src/utils/formatTime';
import {
  fileKindLabel,
  formatFileSize,
} from 'src/constants/supportCaseAttachments';
import type {
  PendingAttachment,
  SupportAttachmentRef,
} from 'src/components/SupportCase/types';
import { PANEL_TOOLTIP_SLOT_PROPS } from 'src/components/SupportCase/panelTooltip';
import FilePreviewDialog from 'src/components/SupportCase/FilePreviewDialog';

const C = 'src/components/SupportCase/AttachmentChips';

/**
 * The files a message carries (ENG-4185).
 *
 * One component, three moments in a file's life, because they are the same idea:
 *
 * * **pending** — chosen in the composer, uploading, removable;
 * * **failed** — chosen, refused, with the sentence saying why;
 * * **sent** — part of an `attachment` event, a link to the stored copy.
 *
 * The pattern is `MailDetailsAttachments` (an outlined chip with a type label and a
 * size), which is what every other attachment surface in Pronto already looks like.
 * Nothing here is coloured by state: a file is not a status, so the chips stay
 * neutral and only the failure row borrows the error colour.
 */

interface AttachmentChipsProps {
  attachments: PendingAttachment[];
  onRemove?: (key: string) => void;
  /** Renders compactly inside a sent message rather than as a composer strip. */
  compact?: boolean;
}

/**
 * One chosen file, and the button that removes it.
 *
 * The remove control is a real `IconButton` beside the chip rather than the chip's own
 * delete affordance: MUI's delete icon is decorative to a screen reader, and "remove
 * this file" is the only action here that has to be reachable by keyboard and by name.
 */
const PendingChip: FC<{
  attachment: PendingAttachment;
  onRemove?: (key: string) => void;
}> = ({ attachment, onRemove }) => (
  <Stack direction="row" spacing={0.25} alignItems="center">
    <Chip
      data-testid={`pending-attachment-${attachment.key}`}
      variant="outlined"
      size="small"
      color={attachment.status === 'failed' ? 'error' : 'default'}
      label={`${attachment.name} · ${fileKindLabel(
        attachment.name
      )} · ${formatFileSize(attachment.size)}`}
      icon={
        attachment.status === 'uploading' ? (
          <CircularProgress size={12} sx={{ ml: 1 }} />
        ) : undefined
      }
      sx={{ maxWidth: '100%' }}
    />
    {onRemove ? (
      <Tooltip
        slotProps={PANEL_TOOLTIP_SLOT_PROPS}
        title={`Remove ${attachment.name}`}
      >
        <IconButton
          size="small"
          onClick={() => onRemove(attachment.key)}
          aria-label={`Remove ${attachment.name}`}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null}
  </Stack>
);

export const PendingAttachmentChips: FC<AttachmentChipsProps> = ({
  attachments,
  onRemove,
  compact = false,
}) => {
  if (attachments.length === 0) {
    return null;
  }
  return (
    <Stack spacing={compact ? 0.25 : 0.5} data-testid={`${C}-pending`}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {attachments.map((attachment) => (
          <PendingChip
            key={attachment.key}
            attachment={attachment}
            onRemove={onRemove}
          />
        ))}
      </Box>
      {attachments
        .filter((attachment) => attachment.error)
        .map((attachment) => (
          <Typography
            key={`${attachment.key}-error`}
            variant="caption"
            color="error.dark"
          >
            {attachment.error}
          </Typography>
        ))}
    </Stack>
  );
};

/**
 * The files a sent message carried.
 *
 * A chip, not a download button, once the file has been removed from the documents
 * library: the event survives the file, so the timeline can still name what was
 * sent. `available` is the answer to "can I still open this", and a removed file
 * says so instead of offering a link that 404s.
 */
export const SentAttachmentChips: FC<{
  attachments: SupportAttachmentRef[];
  availableIds?: Set<number>;
}> = ({ attachments, availableIds }) => {
  /**
   * PA-11 — the chip opens the file here instead of starting a download.
   *
   * The payload carries everything the preview needs except the case, and none of it
   * comes from the detail response: an attachment event names its own file, so a chip
   * works the moment the frame lands, even if the case's document list has not
   * refreshed yet (PA-06).
   */
  const [previewing, setPreviewing] = useState<SupportAttachmentRef | null>(
    null
  );

  if (attachments.length === 0) {
    return null;
  }
  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
      data-testid={`${C}-sent`}
    >
      {attachments.map((attachment) => {
        const available =
          availableIds === undefined || availableIds.has(attachment.id);
        const label = `${attachment.name} · ${fileKindLabel(
          attachment.name
        )} · ${formatFileSize(attachment.size)}`;
        if (!available) {
          return (
            <Chip
              key={attachment.id}
              size="small"
              variant="outlined"
              label={`${label} · removed`}
              data-testid={`sent-attachment-${attachment.id}`}
            />
          );
        }
        return (
          <Chip
            key={attachment.id}
            size="small"
            variant="outlined"
            clickable
            onClick={() => setPreviewing(attachment)}
            label={label}
            data-testid={`sent-attachment-${attachment.id}`}
          />
        );
      })}
      <FilePreviewDialog
        open={previewing !== null}
        onClose={() => setPreviewing(null)}
        document={
          previewing
            ? {
                id: previewing.id,
                name: previewing.name,
                content_type: previewing.content_type || '',
                size: previewing.size || 0,
                origin: previewing.origin ?? 'user',
                case: null,
                case_title: '',
                ready: true,
                deleted: false,
                created_at: null,
              }
            : null
        }
      />
    </Box>
  );
};

/** A "10/28/2026 14:32" caption for the row an attachment hangs off. */
export const AttachmentTimestamp: FC<{ value: string }> = ({ value }) => (
  <Typography variant="caption" color="text.secondary">
    {fDateTime(value)}
  </Typography>
);

export const RemoveAttachmentButton: FC<{
  onClick: () => void;
  label: string;
}> = ({ onClick, label }) => (
  <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title={label}>
    <IconButton size="small" onClick={onClick} aria-label={label}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export default PendingAttachmentChips;
